import { supabase } from './client';
import { authStore } from '@stores/session';
import type { StrainRow } from './queries';

// Strain ratings (migration 0025). A rating is a 1-click 1–5 star vote,
// one row per (profile, strain). Distinct from strain_reviews — reviews
// are structured long-form artefacts, ratings are lightweight signal.
//
// Aggregates live in the `v_strain_rating_summary` view (avg_stars +
// rating_count per strain). The top-rated page applies a count threshold
// at query time so a single 5-star doesn't rocket a strain to #1.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export type StrainRatingSummary = {
  strain_id: string;
  avg_stars: number;
  rating_count: number;
};

export async function fetchStrainRatingSummary(
  strainId: string,
): Promise<StrainRatingSummary | null> {
  const { data, error } = await db
    .from('v_strain_rating_summary')
    .select('strain_id, avg_stars, rating_count')
    .eq('strain_id', strainId)
    .maybeSingle();
  if (error) throw error;
  return (data as StrainRatingSummary | null) ?? null;
}

/**
 * Fetch the current user's rating for a strain, or null if unrated.
 * Returns null silently when signed out — callers render read-only.
 */
export async function fetchMyStrainRating(strainId: string): Promise<number | null> {
  const profile = authStore.profile();
  if (!profile) return null;
  const { data, error } = await db
    .from('strain_ratings')
    .select('stars')
    .eq('profile_id', profile.id)
    .eq('strain_id', strainId)
    .maybeSingle();
  if (error) throw error;
  return (data as { stars: number } | null)?.stars ?? null;
}

/**
 * Upsert the caller's rating. Unique composite (profile_id, strain_id)
 * means a second call updates rather than duplicates — the `touch_*`
 * trigger keeps updated_at fresh server-side.
 */
export async function setStrainRating(strainId: string, stars: number): Promise<void> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Not signed in');
  if (stars < 1 || stars > 5) throw new Error('Stars must be between 1 and 5');
  const { error } = await db
    .from('strain_ratings')
    .upsert(
      { profile_id: profile.id, strain_id: strainId, stars },
      { onConflict: 'profile_id,strain_id' },
    );
  if (error) throw error;
}

export async function removeStrainRating(strainId: string): Promise<void> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Not signed in');
  const { error } = await db
    .from('strain_ratings')
    .delete()
    .eq('profile_id', profile.id)
    .eq('strain_id', strainId);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Top Rated Strains feed
// ---------------------------------------------------------------------------

// Minimum rating count a strain needs before it's eligible for the
// leaderboard — stops a single lucky 5-star from dominating the list
// while the community is still small.
export const TOP_RATED_MIN_COUNT = 3;

export type TopRatedStrain = StrainRow & {
  avg_stars: number;
  rating_count: number;
};

export async function fetchTopRatedStrains(limit = 50): Promise<TopRatedStrain[]> {
  // Step 1 — the summary view, filtered + ordered. This is the source
  // of truth for which strains qualify.
  const { data: summaryData, error: summaryErr } = await db
    .from('v_strain_rating_summary')
    .select('strain_id, avg_stars, rating_count')
    .gte('rating_count', TOP_RATED_MIN_COUNT)
    .order('avg_stars', { ascending: false })
    .order('rating_count', { ascending: false })
    .limit(limit);
  if (summaryErr) throw summaryErr;
  const summary = (summaryData ?? []) as StrainRatingSummary[];
  if (summary.length === 0) return [];

  // Step 2 — hydrate the strain rows. Views don't carry FK metadata in
  // Supabase, so we can't use a nested select here; one follow-up by id.
  const ids = summary.map((r) => r.strain_id);
  const { data: strainData, error: strainErr } = await db
    .from('strains')
    .select('id, name, brand, genetics, category, slug, lineage')
    .in('id', ids)
    .not('excluded_from_listing', 'is', true);
  if (strainErr) throw strainErr;
  const strainById = new Map(
    ((strainData ?? []) as StrainRow[]).map((s) => [s.id, s]),
  );

  // Preserve the summary ordering; drop any summary row whose strain
  // got excluded_from_listing'd between the two queries.
  const rows: TopRatedStrain[] = [];
  for (const s of summary) {
    const strain = strainById.get(s.strain_id);
    if (!strain) continue;
    rows.push({
      ...strain,
      avg_stars: Number(s.avg_stars),
      rating_count: s.rating_count,
    });
  }
  return rows;
}
